import { FC, memo, useEffect, useMemo, useState, useCallback, ReactElement } from 'react'
import { Stream } from 'agora-rtc-sdk'
import { IS_COMPATIBLE } from 'components/features/videoCalls/MicRecorder'
import { useStoreSelector, useStoreDispatch } from 'helpers/reactReduxHelpers'
import { localStreamSelector, videoCallPermissionsStateSelector } from 'components/features/videoCalls/selectors'
import { createAgoraStream } from 'components/features/videoCalls/agoraHelpers'
import { SetMediaPermissionsAction, AgoraAudioInputAction } from 'components/features/videoCalls/types/actions'
import { createIsMediaStreamTrackActiveObservable, getAudioDeviceIdByStream } from 'components/features/coaching/advisorVideoCall/helpersMicCheck'
import { AudioStreamVolumeIndicatorHandler } from 'components/features/coaching/advisorVideoCall/AudioStreamVolumeIndicatorHandler/AudioStreamVolumeIndicatorHandler'
import { QualityIndicator, QUALITY_INDICATOR_MAX_LEVEL } from 'components/features/coaching/advisorVideoCall/QualityIndicator/QualityIndicator'
import { IS_IOS } from 'helpers/userAgentHelpers'
import { captureWarning } from 'helpers/errorHelpers'

// TODO - SM-2739 - for IOS 14+ there is special case with AudioContext - it should be created only after an user's action
const IS_ALLOWED_TO_USE = !IS_IOS && IS_COMPATIBLE

export const TestMicVolume: FC = () => {
  if (!IS_ALLOWED_TO_USE) {
    return null
  }
  return (
    <TestMicVolumeInner />
  )
}

export type RenderQualityIndicatorProps = {
  volumeLevel: number | null
  error: Error | null
}

const RenderQualityIndicator: FC<RenderQualityIndicatorProps> = memo(({ error, volumeLevel }): React.ReactElement | null => {
  useEffect(() => {
    if (error) {
      captureWarning('RenderQualityIndicator: volume.level exception', {
        step: 'volume.level exception',
        message: error.message,
        err: error,
      })
    }
  }, [error])

  if (error) {
    return null
  }

  const volumeIndivatorLevel = Math.ceil(QUALITY_INDICATOR_MAX_LEVEL * Number(volumeLevel))

  return <QualityIndicator level={ volumeIndivatorLevel } />
})

const TestMicVolumeInner: FC = memo(() => {
  const dispatch = useStoreDispatch()
  const [agoraStream, setAgoraStream] = useState<Stream | null>(null)
  const [activeAudioStreamTrack, setActiveAudioStreamTrack] = useState<MediaStreamTrack | null>(null)
  const localStreamState = useStoreSelector(localStreamSelector)
  const videoCallPermissions = useStoreSelector(videoCallPermissionsStateSelector)
  const audioDeviceId = useMemo(() => localStreamState?.audioInputDeviceId, [localStreamState])
  const isUnmute = useMemo(() => localStreamState.audioMuteState === 'unmute', [localStreamState])
  const isAudioPermissionsGiven = videoCallPermissions.mic !== 'denied'
  const closeAndUnsetAgoraStream = useCallback((stream: Stream) => {
    stream.stop()
    stream.close()
    setAgoraStream(null)
  }, [])

  useEffect((): (() => void) | void => {
    if (!isAudioPermissionsGiven || !isUnmute) {
      return
    }

    let isDestroyed = false
    let localStream: Stream | null = null

    createAgoraStream(1, {
      video: false,
      audio: true,
      ...(audioDeviceId ? { microphoneId: audioDeviceId } : {}),
    }).then((nextStream) => {
      if (isDestroyed) {
        closeAndUnsetAgoraStream(nextStream)
        return
      }
      localStream = nextStream
      setAgoraStream(nextStream)

      const nextAudioDeviceId = getAudioDeviceIdByStream(localStream)

      if (audioDeviceId === nextAudioDeviceId) {
        return
      }
      dispatch(new AgoraAudioInputAction(nextAudioDeviceId))
    }).catch((error) => {
      if (isDestroyed) {
        return
      }
      captureWarning('TestMicVolumeInner: volume.level: get audio stream exception', {
        step: 'volume.level: get audio stream exception',
        message: error.message,
        err: error,
      })
      dispatch(new SetMediaPermissionsAction({
        mic: 'denied',
      }))
    })

    return () => {
      isDestroyed = true
      if (!localStream) {
        return
      }
      closeAndUnsetAgoraStream(localStream)
    }
  }, [isAudioPermissionsGiven, isUnmute, audioDeviceId, dispatch, closeAndUnsetAgoraStream])
  useEffect(() => {
    if (!isUnmute || !agoraStream) {
      return
    }

    let audioStreamMediaTrack = (agoraStream.getAudioTrack() || null) as MediaStreamTrack | null

    if (!audioStreamMediaTrack) {
      const error = new Error('Audio stream track should be exist')

      captureWarning('TestMicVolumeInner: audio stream track not exists', {
        step: 'volume.level: audio stream track not exists',
        message: error.message,
        err: error,
      })
      return
    }

    function handleTrackIsInactive () {
      setActiveAudioStreamTrack(null)
    }
    const subscriptionIsAudioStreamTrackActive = createIsMediaStreamTrackActiveObservable(audioStreamMediaTrack).subscribe({
      next: (isStreamTrackActive: boolean): void => {

        if (isStreamTrackActive) {
          setActiveAudioStreamTrack(audioStreamMediaTrack)
        } else {
          handleTrackIsInactive()
        }
      },
      error: (error: Error) => {
        captureWarning('TestMicVolumeInner: audio stream track createIsMediaStreamTrackActiveObservable exception', {
          step: 'volume.level: audio stream track createIsMediaStreamTrackActiveObservable exception',
          message: error.message,
          err: error,
        })
        handleTrackIsInactive()
      },
      complete: handleTrackIsInactive,
    })

    return () => {
      subscriptionIsAudioStreamTrackActive.unsubscribe()
      setActiveAudioStreamTrack(null)
      if (!audioStreamMediaTrack) {
        return
      }
      audioStreamMediaTrack = null
    }
  }, [isUnmute, agoraStream])
  const renderVolume = useCallback((volumeLevel: number | null, error: Error | null): ReactElement | null => {
    return <RenderQualityIndicator volumeLevel={ volumeLevel } error={ error } />
  }, [])
  return (
    <div>
      {isUnmute && agoraStream && activeAudioStreamTrack && (
        <AudioStreamVolumeIndicatorHandler audioStreamTrack={ activeAudioStreamTrack } renderVolumeValueProp={ renderVolume } />
      )}
    </div>
  )
})
